import axiosInstance from './axiosConfig';

/**
 * 管理画面「動画スタイル」API クライアント（/admin/video-style-recipes）。
 * 参考動画を上げると分解（Gemini → Clef → Opus）が非同期で走り、レシピの下書きが draft で出来る。
 * published にしたものだけがユーザーの選択肢に出る。
 */

export type VideoStyleStatus = 'draft' | 'published';
export type VideoStyleAnalysisStatus = 'queued' | 'running' | 'done' | 'failed' | null;
/** スタイル選択の見本を作っているか（null は待機。見本の有無は sample_video_url で見る） */
export type VideoStyleSampleStatus = 'processing' | 'failed' | null;

export interface AdminVideoStyleRecipe {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  status: VideoStyleStatus;
  analysis_status: VideoStyleAnalysisStatus;
  analysis_error: string | null;
  analyzed_at: string | null;
  source_video_url: string | null;
  /** スタイル選択のカードに出す見本（静止画と音なしのダイジェスト）。全ユーザーに出る */
  thumbnail_url: string | null;
  sample_video_url: string | null;
  sample_status: VideoStyleSampleStatus;
  sample_error: string | null;
  /** 見本の元にした完成動画 */
  sample_project_id: number | null;
  scene_count: number;
  caption_style: string | null;
  agreement: { agree: number; judged: number; shots: number } | null;
  projects_count: number;
  /** 生成に使えない点（空なら公開できる） */
  errors: string[];
  created_at: string | null;
  updated_at: string | null;
}

export interface VideoStyleShot {
  start: number;
  end: number;
  role: string;
  kind: string;
  text_style: string;
  visual?: string;
  on_screen_text?: string[];
  speech?: { speaker: string; kind: string; text: string }[];
  clef?: { kind: string | null; text_style: string | null; kind_p?: number | null; model?: string | null } | null;
  agree?: boolean | null;
  frame_url?: string | null;
}

export interface AdminVideoStyleRecipeDetail extends AdminVideoStyleRecipe {
  recipe: Record<string, unknown>;
  analysis: { duration?: number; shots?: VideoStyleShot[]; model?: string; draft_model?: string } | null;
}

export interface VideoStyleRecipeUpdate {
  name?: string;
  description?: string | null;
  recipe?: Record<string, unknown>;
}

/** 試作（管理者が任意の組織の商品で作る。組織のクレジットは使わない） */
export type VideoStyleTrialStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface VideoStyleTrial {
  id: number;
  title: string;
  product_id: number;
  product_name: string | null;
  style_name: string | null;
  status: VideoStyleTrialStatus;
  step: string;
  progress: number;
  thumbnail_url: string | null;
  output_seconds: number | null;
  has_video: boolean;
  created_at: string | null;
  organization_id: number | null;
  organization_name: string | null;
  trial_credits_estimated: number | null;
  trial_credits_used: number;
}

export interface VideoStyleTrialDetail extends VideoStyleTrial {
  error: string | null;
  appeal: string | null;
  video_url: string | null;
  download_url: string | null;
}

export interface VideoStyleTrialList {
  trials: VideoStyleTrial[];
  estimatedCredits: number | null;
  runningTrialId: number | null;
}

export interface TrialProductOption {
  id: number;
  name: string;
  organization_id: number | null;
  organization_name: string | null;
}

export type VideoStyleTrialFilter = 'running' | 'failed' | 'all';

/** 「試作」タブの1行（全スタイル横断） */
export interface VideoStyleTrialListRow extends VideoStyleTrial {
  video_style_recipe_id: number;
  style_status: 'draft' | 'published' | null;
  admin_name: string | null;
}

export interface VideoStyleTrialCounts {
  running: number;
  failed: number;
  all: number;
}

export interface VideoStyleTrialPage {
  trials: VideoStyleTrialListRow[];
  status: VideoStyleTrialFilter;
  page: number;
  lastPage: number;
  total: number;
  counts: VideoStyleTrialCounts;
}

export const isTrialRunning = (trial: { status: VideoStyleTrialStatus }) => trial.status === 'pending' || trial.status === 'running';

const adminVideoStyleRecipeService = {
  async list(): Promise<AdminVideoStyleRecipe[]> {
    const response = await axiosInstance.get('/admin/video-style-recipes');
    return response.data.data ?? [];
  },

  async show(id: number): Promise<AdminVideoStyleRecipeDetail> {
    const response = await axiosInstance.get(`/admin/video-style-recipes/${id}`);
    return response.data.data;
  },

  async create(input: { name: string; description?: string; file: File }): Promise<AdminVideoStyleRecipe> {
    const form = new FormData();
    form.append('name', input.name);
    if (input.description) form.append('description', input.description);
    form.append('file', input.file);
    const response = await axiosInstance.post('/admin/video-style-recipes', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data.data;
  },

  async update(id: number, input: VideoStyleRecipeUpdate): Promise<AdminVideoStyleRecipeDetail> {
    const response = await axiosInstance.put(`/admin/video-style-recipes/${id}`, input);
    return response.data.data;
  },

  async toggle(id: number): Promise<AdminVideoStyleRecipe> {
    const response = await axiosInstance.put(`/admin/video-style-recipes/${id}/toggle`);
    return response.data.data;
  },

  async reanalyze(id: number): Promise<AdminVideoStyleRecipe> {
    const response = await axiosInstance.post(`/admin/video-style-recipes/${id}/reanalyze`);
    return response.data.data;
  },

  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`/admin/video-style-recipes/${id}`);
  },

  /** 完成動画から見本を作り直す（作るのは非同期。sample_status が processing になる） */
  async setSample(id: number, projectId: number): Promise<AdminVideoStyleRecipe> {
    const response = await axiosInstance.post(`/admin/video-style-recipes/${id}/sample`, { project_id: projectId });
    return response.data.data;
  },

  async removeSample(id: number): Promise<AdminVideoStyleRecipe> {
    const response = await axiosInstance.delete(`/admin/video-style-recipes/${id}/sample`);
    return response.data.data;
  },

  async trials(id: number): Promise<VideoStyleTrialList> {
    const response = await axiosInstance.get(`/admin/video-style-recipes/${id}/trials`);
    return {
      trials: response.data.data ?? [],
      estimatedCredits: response.data.meta?.estimated_credits ?? null,
      runningTrialId: response.data.meta?.running_trial_id ?? null,
    };
  },

  async startTrial(id: number, input: { product_id: number; appeal?: string | null }): Promise<VideoStyleTrialDetail> {
    const response = await axiosInstance.post(`/admin/video-style-recipes/${id}/trials`, input);
    return response.data.data;
  },

  async trialList(params: { status: VideoStyleTrialFilter; page?: number }): Promise<VideoStyleTrialPage> {
    const response = await axiosInstance.get('/admin/recipe-video-trials', { params: { status: params.status, page: params.page ?? 1 } });
    const meta = response.data.meta ?? {};
    return {
      trials: response.data.data ?? [],
      status: meta.status ?? params.status,
      page: meta.page ?? 1,
      lastPage: meta.last_page ?? 1,
      total: meta.total ?? 0,
      counts: meta.counts ?? { running: 0, failed: 0, all: 0 },
    };
  },

  async trial(projectId: number): Promise<VideoStyleTrialDetail> {
    const response = await axiosInstance.get(`/admin/recipe-video-trials/${projectId}`);
    return response.data.data;
  },

  async searchProducts(q: string): Promise<TrialProductOption[]> {
    const response = await axiosInstance.get('/admin/recipe-video-trials/products', { params: { q } });
    return response.data.data ?? [];
  },

  async productAppeals(productId: number): Promise<{ name: string; direction: string | null }[]> {
    const response = await axiosInstance.get(`/admin/recipe-video-trials/products/${productId}/appeals`);
    return response.data.data ?? [];
  },
};

/** API のエラー応答から、画面に出す文（message と検証の指摘）を取り出す */
export const videoStyleErrorMessages = (error: unknown, fallback: string): string[] => {
  const data = (error as { response?: { data?: { message?: string; errors?: unknown } } })?.response?.data;
  const lines: string[] = [];
  if (data?.message) lines.push(data.message);
  const errors = data?.errors;
  if (Array.isArray(errors)) lines.push(...errors.map(String));
  else if (errors && typeof errors === 'object') {
    Object.values(errors as Record<string, unknown>).forEach((value) => {
      if (Array.isArray(value)) lines.push(...value.map(String));
    });
  }
  return lines.length > 0 ? lines : [fallback];
};

export default adminVideoStyleRecipeService;
